import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '../lib/cn';

/**
 * The button.
 *
 * Seven variants, four sizes, one focus ring. Every clickable affordance in the
 * application resolves to this so a destructive action never looks like a
 * primary one on one screen and a link on another.
 */

const buttonVariants = cva(
  cn(
    'inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-md',
    'font-medium transition-colors duration-100',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-1',
    'disabled:pointer-events-none disabled:opacity-50',
  ),
  {
    variants: {
      variant: {
        primary: 'bg-brand text-white shadow-xs hover:bg-brand-hover active:bg-brand-hover',
        secondary:
          'border border-line-strong bg-surface text-ink shadow-xs hover:bg-surface-muted active:bg-neutral-soft',
        ghost: 'text-ink-secondary hover:bg-neutral-soft hover:text-ink',
        subtle: 'bg-neutral-soft text-ink-secondary hover:bg-line',
        danger: 'bg-danger text-white shadow-xs hover:brightness-95',
        'danger-outline':
          'border border-danger-border bg-surface text-danger shadow-xs hover:bg-danger-soft',
        link: 'text-brand underline-offset-4 hover:underline',
      },
      size: {
        // 28px — dense table row actions only.
        xs: 'h-7 px-2 text-caption',
        sm: 'h-8 px-2.5 text-sm',
        md: 'h-9 px-3.5 text-base',
        lg: 'h-10 px-4 text-base',
        // Square icon buttons keep a real hit target rather than shrinking to the glyph.
        icon: 'h-9 w-9 p-0',
        'icon-sm': 'h-8 w-8 p-0',
      },
      block: { true: 'w-full', false: '' },
    },
    defaultVariants: { variant: 'secondary', size: 'md', block: false },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Render as the child element (a router link, for example). */
  asChild?: boolean;
  /** Shows a spinner and blocks interaction without changing the button's width. */
  loading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant,
    size,
    block,
    asChild = false,
    loading = false,
    leadingIcon,
    trailingIcon,
    children,
    disabled,
    ...props
  },
  ref,
) {
  const spinner = <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />;
  const leading = loading ? spinner : leadingIcon;
  const trailing = loading ? null : trailingIcon;
  const classes = cn(buttonVariants({ variant, size, block }), className);

  if (asChild) {
    // The children are passed to `Slot` individually, never wrapped in a
    // fragment. `Slot` merges its props into its single element child, so a
    // fragment would swallow the className and the button would render
    // unstyled — and `Slottable` is what tells `Slot` which of the three
    // children is the one to merge into.
    return (
      <Slot ref={ref} className={classes} aria-busy={loading || undefined} {...props}>
        {leading}
        <Slottable>{children}</Slottable>
        {trailing}
      </Slot>
    );
  }

  return (
    <button
      ref={ref}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {leading}
      {children}
      {trailing}
    </button>
  );
});

export { buttonVariants };
